import {
    type FC,
    type MouseEvent,
    type RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { useShopfrontDispatch } from "../../../_Bootstrap/hooks";
import { type DialogOptions, showDialog } from "../../../Actions/Dialogs/DialogActions";
import BackButton from "../../../Common/Buttons/BackButton";
import { CancelOptionButton } from "../../../Common/Buttons/Button";
import { usePrompt } from "../../../Common/Dialogs/NavigationBlock";
import { ScreenSizeError } from "../../../Common/Errors/ScreenSizeError";
import { type GridCursorType } from "../../../Common/Forms/GridEditor/Cursor/GridCursor";
import { GridEditor, type GridItemType } from "../../../Common/Forms/GridEditor/GridEditor";
import { GridEditorBoundary } from "../../../Common/Forms/GridEditor/GridEditorBoundary";
import LoadingScreen from "../../../Common/Loading/LoadingScreen";
import PopupManager from "../../../Common/Popups/PopupManager";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import { type ReportClauses } from "../../../Utilities/Domain/Reports/ReportParser";
import UUID from "../../../Utilities/Generic/UUID";
import { parseReport } from "../../../Utilities/Platform/Reports";
import Request from "../../../Utilities/Platform/Request";
import { DASHBOARD_ROW_HEIGHT, type DashboardItemType, type DashboardReport } from "../Dashboard";
import { DashboardActions } from "../DashboardActions";
import { DashboardConfigure } from "../DashboardConfigure";
import { DashboardApplyDialog } from "./DashboardApplyDialog";
import { DashboardModifyItem } from "./DashboardModifyItem";

interface Props {
    smallScreen: boolean;
    user: string;
    items: Array<DashboardItemType>;
    onCancel: () => void;
    onSave: (items: Array<DashboardItemType>, reports: Array<DashboardReport>) => void;
}

export type DashboardItemEditType = {
    report: null | string;
    filterUser: boolean;
};

export interface ReportOptionType {
    value: string;
    label: string;
    additional: {
        type: string;
        report: ReportClauses;
    };
}

type ItemValidateMap = Record<number, Record<number, Set<string>>>;

/**
 * Add the item to the validation map
 * @param mapRef
 * @param item
 */
function addItemToMap(mapRef: RefObject<ItemValidateMap>, item: GridItemType): void {
    if(!mapRef.current) {
        return;
    }

    for(let x = item.left, l = item.left + item.width; x < l; x++) {
        if(typeof mapRef.current[x] === "undefined") {
            mapRef.current[x] = {};
        }

        for(let y = item.top, l = item.top + item.height; y < l; y++) {
            if(typeof mapRef.current[x][y] === "undefined") {
                mapRef.current[x][y] = new Set<string>();
            }

            mapRef.current[x][y].add(item.id);
        }
    }
}

/**
 * Remove the item from the validation map
 * @param mapRef
 * @param item
 */
function removeItemFromMap(mapRef: RefObject<ItemValidateMap>, item: GridItemType): void {
    if(!mapRef.current) {
        return;
    }

    for(let x = item.left, l = item.left + item.width; x < l; x++) {
        if(typeof mapRef.current[x] === "undefined") {
            continue;
        }

        for(let y = item.top, l = item.top + item.height; y < l; y++) {
            if(typeof mapRef.current[x][y] === "undefined") {
                continue;
            }

            mapRef.current[x][y].delete(item.id);
        }
    }
}

const GRID_WIDTH = 10;

/**
 * Find the first free space on the
 * @param height
 * @param map
 */
function findFreeSpace(height: number, map: ItemValidateMap): [ x: number, y: number ] {
    for(let y = 1; y <= height; y++) {
        for(let x = 1; x <= GRID_WIDTH; x++) {
            if(typeof map[x] === "undefined") {
                // Coords start at 1
                return [ x, 1 ];
            }

            if(typeof map[x][y] === "undefined") {
                return [ x, y ];
            }

            if(!map[x][y].size) {
                return [ x, y ];
            }
        }
    }

    return [ 1, height + 1 ];
}

export const DashboardModify: FC<Props> = ({
    smallScreen,
    user,
    items: itemsProp,
    onCancel,
    onSave: onSaveComplete,
}) => {
    const validateMapRef = useRef<ItemValidateMap>({});

    const dispatch = useShopfrontDispatch();

    const [ loading, setLoading ] = useState(false);
    const [ reports, setReports ] = useState<Array<ReportOptionType>>([]);

    const [ items, setItems ] = useState<Array<GridItemType>>([]);
    const [ map, setMap ] = useState<Record<string, DashboardItemEditType>>({});

    const [ selected, setSelected ] = useState<null | string>(null);
    const [ applyTo, setApplyTo ] = useState<Array<string>>([]);
    const [ height, setHeight ] = useState<number | null>(null);
    const [ lowest, setLowest ] = useState(0);
    const [ errors, setErrors ] = useState<Record<string, boolean>>({});
    const [ isDirty, setIsDirty ] = useState(false);

    useEffect(() => {
        const items: Array<GridItemType> = [];
        const map: Record<string, DashboardItemEditType> = {};

        validateMapRef.current = {};

        for(const { position, ...dashboardItem } of itemsProp) {
            const id = UUID.generate();

            const item: GridItemType = {
                id,
                top   : position.y + 1,
                left  : position.x + 1,
                width : position.w,
                height: position.h,
            };

            items.push(item);

            addItemToMap(validateMapRef, item);

            map[id] = dashboardItem;
        }

        setItems(items);
        setMap(map);
    }, [ itemsProp ]);

    useEffect(() => {
        let lowest = 0;

        for(const item of items) {
            // Note: minus one because css grid starts at 1
            const bottom = item.top + item.height - 1;

            if(lowest < bottom) {
                lowest = bottom;
            }
        }

        setLowest(lowest);
    }, [ items ]);

    useEffect(() => {
        const favoriteRequest = Request.post<{
            errors?: unknown;
            data: {
                reports: Array<{
                    id: string;
                    name: string;
                    type?: string;
                    report?: string;
                }>;
            };
        }>("/graphql", {
            query: `query GetReports {
                reports {
                    id,
                    name,
                    ...on FavouriteReport {
                        type,
                        report,
                    },
                }
            }`,
        })
            .then(response => {
                if(response.data.errors) {
                    throw response.data.errors;
                }

                return response.data.data.reports;
            })
            .catch(err => {
                console.error("Error loading favourite reports", err);

                return [];
            });

        const popularRequest = Request.get<Array<{
            id: number;
            name: string;
            description: string;
            query: string;
        }>>("/reports/popular")
            .then(response => {
                return response.data;
            })
            .catch(err => {
                console.error("Error loading popular reports", err);

                return [];
            });

        Promise.all([
            favoriteRequest,
            popularRequest,
        ])
            .then(([ favoriteReports, popularReports ]) => {
                const reports: Array<ReportOptionType> = [];

                // Prepare favorite reports
                for(const report of favoriteReports) {
                    if(typeof report.report !== "string") {
                        continue;
                    }

                    try {
                        const parsed = parseReport(report.report);

                        reports.push({
                            label     : report.name,
                            value     : report.id,
                            additional: {
                                type  : report.type as string,
                                report: parsed,
                            },
                        });
                    } catch(err) {
                        console.error("Error parsing favorite report", err);
                    }
                }

                // Prepare popular reports
                for(const report of popularReports) {
                    try {
                        const parsed = parseReport(report.query);

                        reports.push({
                            label     : report.name,
                            value     : report.id.toString(),
                            additional: {
                                type  : "popular",
                                report: parsed,
                            },
                        });
                    } catch(err) {
                        console.error("Error parsing popular report", err);
                    }
                }

                setLoading(false);
                setReports(reports);
            })
            .catch(err => {
                console.error("Failed to load reports", err);
                setLoading(false);
                setReports([]);
            });
    }, []);

    const onValidate = useCallback((id: string, cursor: GridCursorType) => {
        if(!validateMapRef.current) {
            return false;
        }

        for(let x = cursor.left, l = cursor.left + cursor.width; x < l; x++) {
            if(typeof validateMapRef.current[x] === "undefined") {
                continue;
            }

            for(let y = cursor.top, l = cursor.top + cursor.height; y < l; y++) {
                if(typeof validateMapRef.current[x][y] === "undefined") {
                    continue;
                }

                // The cell will have more than the current item
                if(validateMapRef.current[x][y].size > 1) {
                    return false;
                } else if(validateMapRef.current[x][y].size === 1) {
                    if(!validateMapRef.current[x][y].has(id)) {
                        return false;
                    }
                }
            }
        }

        return true;
    }, []);

    usePrompt(isDirty && !smallScreen, "You have unsaved changed, are you sure you wish to leave?");

    const onDelete = useCallback((id: string) => {
        const item = items.find(item => item.id === id);

        if(!item) {
            return;
        }

        removeItemFromMap(validateMapRef, item);

        setItems(prevState => {
            const index = prevState.findIndex(item => item.id === id);

            if(index < 0) {
                return prevState;
            }

            return [
                ...prevState.slice(0, index),
                ...prevState.slice(index + 1),
            ];
        });

        setMap(prevState => {
            const { [id]: _, ...rest } = prevState;

            return rest;
        });

        setErrors(prevState => {
            const { [id]: _, ...rest } = prevState;

            return rest;
        });

        setIsDirty(true);
        setSelected(prevState => prevState !== id ? prevState : null);
    }, [ items ]);

    if(smallScreen) {
        return (
            <ScreenSizeError>
                <p>The current screen size is too small for editing the dashboard</p>
                <div>
                    <BackButton onClick={onCancel} />
                </div>
            </ScreenSizeError>
        );
    }

    if(loading) {
        return (
            <LoadingScreen>
                Loading Dashboard Editor...
            </LoadingScreen>
        );
    }

    const onAdd = () => {
        if(!validateMapRef.current) {
            return;
        }

        const [ x, y ] = findFreeSpace(lowest, validateMapRef.current);

        const id = UUID.generate();

        const item: GridItemType = {
            id,
            top   : y,
            left  : x,
            width : 1,
            height: 1,
        };

        addItemToMap(validateMapRef, item);

        setItems(prevState => [
            ...prevState,
            item,
        ]);

        setMap(prevState => ({
            ...prevState,
            [id]: {
                report    : null,
                filterUser: false,
            },
        }));

        setSelected(id);
        setIsDirty(true);
    };

    const handleDeleteClick = () => {
        if(!selected) {
            return;
        }

        onDelete(selected);
    };

    const finalizeSave = async (value: Array<DashboardItemType>, reports: Array<DashboardReport>): Promise<void> => {
        const ids = [ ...new Set([ user, ...applyTo ]) ];
        const dashboard = JSON.stringify(value);

        try {
            await Request.post<{
                errors?: unknown;
                updateUserDashboard: boolean;
            }>("/graphql", {
                query: `mutation UpdateDashboard($ids: [ID]!, $dashboard: JSON!) {
                    updateUserDashboard(ids: $ids, dashboard: $dashboard)
                }`,
                variables: {
                    ids,
                    dashboard,
                },
            })
                .then(response => {
                    if(response.data.errors) {
                        throw response.data.errors;
                    }
                });

            onSaveComplete(value, reports);
            setIsDirty(false);

            const source = await determineDataSource();

            for(const id of ids) {
                await source.users.update(id, {
                    dashboard,
                });
            }
        } catch(err) {
            console.error("Failed to update dashboard", err);
            PopupManager.error("Failed to update dashboard");

            return;
        }
    };

    const onSave = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        const errors: Array<string> = [];
        const dashboard: Array<DashboardItemType> = [];
        const reportMap: Record<string, DashboardReport> = {};

        for(const gridItem of items) {
            const item = map[gridItem.id];

            if(!item) {
                continue;
            }

            if(!item.report) {
                errors.push(gridItem.id);
                continue;
            } else if(errors.length) {
                continue;
            }

            if(!reportMap[item.report]) {
                const report = reports.find(report => report.value === item.report);

                if(!report) {
                    continue;
                }

                reportMap[item.report] = {
                    name  : report.label,
                    report: report.additional.report,
                    type  : report.additional.type,
                    id    : item.report,
                };
            }

            dashboard.push({
                report    : item.report,
                filterUser: item.filterUser,
                position  : {
                    x: gridItem.left - 1,
                    y: gridItem.top - 1,
                    w: gridItem.width,
                    h: gridItem.height,
                },
            });
        }

        if(errors.length) {
            PopupManager.error(
                errors.length === 1 ?
                    "A dashboard item is in an invalid state" :
                    "Dashboard items are in an invalid state"
            );

            setErrors(errors.reduce<Record<string, boolean>>((map, id) => {
                map[id] = true;

                return map;
            }, {}));

            return;
        }

        if(applyTo.length) {
            dispatch(showDialog(
                "Save to Additional",
                <DashboardApplyDialog applyTo={applyTo} />,
                "question",
                [ CancelOptionButton, {
                    label   : "Continue",
                    classes : [ "button-save" ],
                    callback: () => {
                        void finalizeSave(dashboard, Object.values(reportMap));
                    },
                }]
            ));
        } else {
            void finalizeSave(dashboard, Object.values(reportMap));
        }
    };

    return (
        <>
            <GridEditorBoundary className="mr-12 border border-black dark:border-white">
                <GridEditor
                    width={GRID_WIDTH}
                    height={Math.max(height === null ? lowest + 1 : Math.max(height, lowest + 1), 5)}
                    rowSize={DASHBOARD_ROW_HEIGHT}
                    items={items}
                    onChange={(id, cursor) => {
                        const item = items.find(item => item.id === id);

                        if(!item) {
                            return;
                        }

                        const newItem = {
                            ...item,
                            ...cursor,
                        };

                        setIsDirty(true);
                        removeItemFromMap(validateMapRef, item);
                        addItemToMap(validateMapRef, newItem);

                        setItems(prevState => {
                            const index = prevState.findIndex(item => item.id === id);

                            if(index < 0) {
                                return prevState;
                            }

                            return [
                                ...prevState.slice(0, index),
                                newItem,
                                ...prevState.slice(index + 1),
                            ];
                        });
                    }}
                    onDelete={onDelete}
                    onValid={onValidate}
                    renderItem={(item) => {
                        const report = map[item.id];

                        return (
                            <DashboardModifyItem
                                options={reports}
                                report={report?.report ?? null}
                                error={errors[item.id] ?? false}
                                filterUser={report?.filterUser ?? false}
                                onChange={report => {
                                    setIsDirty(true);
                                    setMap(prevState => ({
                                        ...prevState,
                                        [item.id]: report,
                                    }));

                                    // Remove the error value
                                    setErrors(prevState => {
                                        if(typeof prevState[item.id] === "undefined") {
                                            return prevState;
                                        }

                                        const { [item.id]:_, ...rest } = prevState;

                                        return rest;
                                    });
                                }}
                            />
                        );
                    }}
                    selected={selected}
                    onSelect={setSelected}
                />
            </GridEditorBoundary>
            <DashboardActions
                actions={[{
                    classes: "bg-sky-600",
                    icon   : "save",
                    label  : "Save",
                    onClick: onSave,
                }, {
                    classes: "bg-red-600",
                    icon   : "times",
                    label  : "Cancel",
                    onClick: (e) => {
                        if(!isDirty) {
                            onCancel();

                            return;
                        }

                        e.stopPropagation();

                        const options: Array<DialogOptions> = [{
                            label  : "No",
                            classes: [ "button-cancel" ],
                        }, {
                            label   : "Yes",
                            classes : [ "button-danger" ],
                            callback: onCancel,
                        }];

                        dispatch(showDialog(
                            "Discard Changes",
                            "Are you sure you wish to discard the changes to the user dashboard?",
                            "warning",
                            options,
                            false
                        ));
                    },
                }, {
                    classes: "bg-neutral-700",
                    icon   : "cogs",
                    label  : "Configure",
                    onClick: (e) => {
                        e.stopPropagation();

                        dispatch(showDialog(
                            "Configure Dashboard",
                            <DashboardConfigure
                                users={[]}
                                lowest={lowest}
                                height={height}
                                onConfirm={(users, height) => {
                                    setHeight(height);
                                    setApplyTo(users);
                                }}
                            />,
                            "question"
                        ));
                    },
                }, {
                    classes: "bg-green-600",
                    icon   : "plus",
                    label  : "Add Item",
                    onClick: onAdd,
                }, {
                    disabled: selected === null,
                    classes : "bg-red-600",
                    icon    : "trash-alt",
                    label   : "Delete",
                    onClick : handleDeleteClick,
                }]}
            />
        </>
    );
};
